import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  ListItemIcon,
  Radio,
  Checkbox,
  Button,
} from "@mui/material";
import {
  TableBorderRadiusTopLeftRight,
  StyledTableCell,
  pageSize,
  TablePaginationStyle,
  paginationLabel,
  NoDataContainer,
} from "../Common/styles/table";
import http from "../../utils/http";
import { useNavigate } from "react-router-dom";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import DownloadIcon from "@mui/icons-material/Download";
import { FilterAlt, MoreVert } from "@mui/icons-material";
import VerifyConsentModal from "../ParticipantRecords/ParticipantDetails/ConsentForms/VerifyConsentModal";
import StatusBadge from "../Common/UI/StatusTag";

const statusMap: any = {
  in_progress: { label: "Inprogress", variant: "pending" },
  signed: { label: "Participant Signed", variant: "active" },
  not_started: { label: "Sent", variant: "sent" },
  provider_signed: { label: "Completed", variant: "sent" },
  verified: { label: "Verified", variant: "sent" },
};

type Props = {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  formType: "consent" | "sunshine";
};

const PlatformConsentsTracking = ({
  page,
  setPage,
  filter,
  setFilter,
  formType,
}: Props) => {
  const navigate = useNavigate();

  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState(0);
  const [toggleLoader, setToggleLoader] = useState<boolean>(false);

  const [loading, setLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");
  const [selectedParticipantId, setSelectedParticipantId] =
    useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [filterEl, setFilterEl] = useState<null | HTMLElement>(null);
  const openFilter = Boolean(filterEl);
  const [selectedConsents, setSelectedConsents] = useState<Set<string>>(
    new Set()
  );
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url = `/platform/admin/econsent/participants/econsents?page=${
          page + 1
        }&size=${pageSize}`;
        if (filter) {
          url += `&status=${filter}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.data?.map((item: any, index: number) => ({
          id: item?.consentVersion?.id,
          formId: item?.id,
          name: item?.consentVersion?.consent?.name,
          participantName: item?.participant?.firstName
            ? item?.participant?.lastName
              ? `${item?.participant?.firstName} ${item?.participant?.lastName}`
              : item?.participant?.firstName
            : "",
          version: item?.consentVersion?.version,
          consentId: item?.consentVersion?.consent?.id,
          consentDate: item?.signedOn
            ? DateTime.fromISO(item?.signedOn).toFormat("MM/dd/yyyy")
            : "-",
          status: item?.status,
          sentOn: item?.sentOn
            ? DateTime.fromISO(item?.sentOn).toFormat("MMM dd, yyyy")
            : "-",
          participantId: item?.participant?.id,
          subjectId: item?.participant?.studyParticipant?.subjectId,
        }));

        setData(newData || []);
        setDataCount(data?.meta?.paginationOptions?.totalItems || 0);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [page, toggleLoader, filter]);

  useEffect(() => {
    setSelectedConsents(new Set());
  }, [formType]);

  const handleDownload = async (participantId: string, versionId: string) => {
    try {
      setDownloadingRowId(versionId);
      setSelectedParticipantId(participantId);
      let res = await http.get(
        `/platform/admin/econsent/participants/${participantId}/signed-econsents/${versionId}/download`
      );
      const downloadLink = res?.data?.data?.downloadUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
      setSelectedParticipantId("");
    } catch (err) {
      setDownloadingRowId("");
      setSelectedParticipantId("");
      errorToastMessage(err as Error);
    }
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const resendConsent = async (participantId: string, versionId: string) => {
    try {
      setDownloadingRowId(versionId);
      setSelectedParticipantId(participantId);

      let body: any = {
        versionId: versionId,
      };
      let res = await http.post(
        `/platform/admin/econsent/participants/${participantId}/resend`,
        body
      );
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
      setSelectedParticipantId("");
    } catch (err) {
      setDownloadingRowId("");
      setSelectedParticipantId("");
      errorToastMessage(err as Error);
    }
  };

  const handleSignConsent = (
    participantId: string,
    consentId: string,
    versionId: string,
    status: string
  ) => {
    if (status === "signed") {
      navigate(
        `/app/participants/${participantId}/consent-form/${consentId}?versionId=${versionId}`
      );
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openVerifyModal = () => {
    setAnchorEl(null);
    setShowVerifyModal(true);
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setSelectedRow(null);
  };

  const openFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterEl(event.currentTarget);
  };

  const handleFilter = (value: string) => {
    setFilter(value);
    setFilterEl(null);
    setPage(0);
  };
  const clearFilter = () => {
    setFilter("");
    setFilterEl(null);
  };

  const closeFilterMenu = () => {
    setFilterEl(null);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => {
        const newConsentIds = data
          .filter(
            (item: any) =>
              item?.status === "signed" ||
              item?.status === "provider_signed" ||
              item?.status === "verified"
          )
          .map((item: any) => item.formId);
        return new Set([...prev, ...newConsentIds]);
      });
    }
  };

  const handleSelectRow = (
    event: React.ChangeEvent<HTMLInputElement>,
    consentId: string
  ) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => new Set([...prev, consentId]));
    } else {
      setSelectedConsents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(consentId);
        return newSet;
      });
    }
  };

  const isRowSelected = (consentId: string) => {
    return selectedConsents.has(consentId);
  };

  const handleBulkDownload = async () => {
    if (selectedConsents.size === 0) {
      toastMessage(
        "warning",
        `Please select at least one ${
          formType === "consent" ? "consent" : "form"
        } to download`
      );
      return;
    }

    try {
      setBulkDownloadLoading(true);

      const participantEconsentIds = Array.from(selectedConsents);

      const res = await http.post(
        `/platform/admin/econsent/participants/econsents/bulk-download`,
        { participantEconsentIds },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${
        formType === "consent" ? "consents" : "forms"
      }-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastMessage(
        "success",
        `${selectedConsents.size} ${
          formType === "consent" ? "consent" : "form"
        }(s) downloaded successfully`
      );
      setSelectedConsents(new Set());
      setBulkDownloadLoading(false);
    } catch (err) {
      setBulkDownloadLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <>
      <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
        {selectedConsents.size > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="body1" fontWeight="medium">
              {selectedConsents.size}{" "}
              {formType === "consent" ? "consent" : "form"}(s) selected
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setSelectedConsents(new Set())}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                startIcon={
                  bulkDownloadLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleBulkDownload}
                disabled={bulkDownloadLoading}
              >
                Download Selected
              </Button>
            </Box>
          </Box>
        )}
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox checked={false} onChange={handleSelectAll} />
                    <Typography fontWeight={"bold"} variant="body2">
                      Participant Name
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  {formType === "consent" ? "Consent Name" : "Form Name"}
                </StyledTableCell>
                <StyledTableCell>
                  {formType === "consent"
                    ? "Provider Consent"
                    : "Provider Signature"}
                </StyledTableCell>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>
                  {formType === "consent" ? "Consent Date" : "Form Date"}
                </StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton onClick={openFilterMenu}>
                    <FilterAlt color={filter === "" ? "disabled" : "primary"} />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {!loading && data?.length > 0 && (
              <>
                <TableBody>
                  {data?.map((item: any, index: number) => {
                    const isSelectable =
                      item?.status === "signed" ||
                      item?.status === "provider_signed" ||
                      item?.status === "verified";
                    const isSelected = isRowSelected(item.formId);

                    return (
                      <TableRow key={index}>
                        <StyledTableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={!isSelectable}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleSelectRow(e, item.formId)}
                            />
                            <Typography
                              variant="body1"
                              fontWeight={"regular"}
                              sx={{ flexShrink: 0 }}
                            >
                              {item?.participantName || item?.subjectId || "-"}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>{item?.name}</StyledTableCell>
                        <StyledTableCell>
                          <Typography
                            onClick={() =>
                              handleSignConsent(
                                item?.participantId,
                                item?.consentId,
                                item?.id,
                                item?.status
                              )
                            }
                            sx={{
                              cursor:
                                item?.status === "signed" ? "pointer" : "auto",
                              color:
                                item?.status === "signed"
                                  ? "primary.main"
                                  : "text.primary",
                            }}
                          >
                            {item?.status === "provider_signed" ||
                            item?.status === "verified"
                              ? "Signed"
                              : item?.status === "signed"
                              ? formType === "consent"
                                ? "Sign Consent"
                                : "Sign Form"
                              : "-"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>{item?.version}</StyledTableCell>
                        <StyledTableCell>{item?.consentDate}</StyledTableCell>
                        <StyledTableCell>
                          <StatusBadge
                            label={
                              statusMap[item?.status]?.label ?? item?.status
                            }
                            variant={statusMap[item?.status]?.variant}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {downloadingRowId === item?.id &&
                          selectedParticipantId === item?.participantId ? (
                            <CircularProgress size={18} sx={{ ml: 1 }} />
                          ) : item?.status === "signed" ||
                            item?.status === "provider_signed" ||
                            item?.status === "verified" ? (
                            <IconButton
                              onClick={() =>
                                handleDownload(item?.participantId, item?.id)
                              }
                            >
                              <DownloadIcon />
                            </IconButton>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "14px",
                                fontWeight: "regular",
                                color: "primary.main",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                resendConsent(item?.participantId, item?.id)
                              }
                            >
                              Remind Again
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {(item?.status === "provider_signed" ||
                            item?.status === "signed") && (
                            <IconButton
                              onClick={(e) => handleMenuClick(e, item)}
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {dataCount > pageSize && (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        sx={TablePaginationStyle}
                        count={dataCount}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[pageSize]}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={paginationLabel}
                      />
                    </TableRow>
                  </TableFooter>
                )}
              </>
            )}
          </Table>
          {!loading && data.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No Data
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={openVerifyModal}>
          <ListItemText>
            {formType === "consent" ? "Verify Consent" : "Verify Form"}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={filterEl}
        open={openFilter}
        onClose={closeFilterMenu}
        sx={{
          "& .MuiPaper-root": {
            width: 200,
          },
        }}
      >
        {Object.keys(statusMap).map((key) => (
          <MenuItem key={key} onClick={() => handleFilter(key)}>
            <ListItemIcon>
              <Radio checked={filter === key} />
            </ListItemIcon>
            <ListItemText>{statusMap?.[key]?.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={clearFilter}>Clear</MenuItem>
      </Menu>

      {showVerifyModal && (
        <VerifyConsentModal
          show={showVerifyModal}
          onClose={closeVerifyModal}
          data={selectedRow}
          refreshPage={refreshPage}
          participantIdForModal={selectedRow?.participantId}
          type="platform"
          formType={formType}
        />
      )}
    </>
  );
};

export default PlatformConsentsTracking;
